import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Shell from '../components/Shell';
import { C2Button, C2Card, C2Section } from '../components/c2';
import type { DecorShape } from '../components/c2/Section';
import type { PastelColor } from '../components/c2/Pill';
import PageHero from '../components/secondary/PageHero';
import { api, type ApiError } from '../lib/api';

const cardsDecor: DecorShape[] = [
  { shape: 'circle', size: 220, color: 'lemon', style: { position: 'absolute', top: 40, left: -90, opacity: 0.45 } },
  { shape: 'pill', size: 220, color: 'lilac', style: { position: 'absolute', top: 200, right: -70, opacity: 0.45, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 130, color: 'mint', style: { position: 'absolute', bottom: 40, left: '15%', opacity: 0.45, transform: 'rotate(-10deg)' } },
];

const formDecor: DecorShape[] = [
  { shape: 'circle', size: 240, color: 'peach', style: { position: 'absolute', top: 60, left: -100, opacity: 0.45 } },
  { shape: 'pill', size: 220, color: 'sky', style: { position: 'absolute', top: 200, right: -70, opacity: 0.45, transform: 'rotate(-8deg)' } },
  { shape: 'square', size: 130, color: 'mint', style: { position: 'absolute', bottom: 60, left: '12%', opacity: 0.45, transform: 'rotate(10deg)' } },
  { shape: 'circle', size: 180, color: 'lemon', style: { position: 'absolute', bottom: 40, right: -50, opacity: 0.45 } },
];

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '14px 16px',
  border: '1px solid var(--color-hairline-strong)',
  borderRadius: 14,
  fontSize: 15,
  background: 'var(--color-surface)',
  color: 'var(--color-ink)',
  outline: 'none',
  transition: 'border-color 140ms ease, box-shadow 140ms ease',
  fontFamily: 'inherit',
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span
        className="font-mono uppercase"
        style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

interface CardDef {
  icon: string;
  title: string;
  desc: string;
  cta: string;
  to?: string;
  href?: string;
  color: PastelColor;
}

const cards: CardDef[] = [
  { icon: '✉', title: 'Email us', desc: 'Real humans. Usually same-day replies, always within 24 hours.', cta: 'support@tidygolinks.com', href: 'mailto:support@tidygolinks.com', color: 'peach' },
  { icon: '📚', title: 'Browse the FAQ', desc: 'Quick answers to the questions everyone asks.', cta: 'Read FAQ', to: '/faq', color: 'sky' },
  { icon: '🐛', title: 'Found a bug?', desc: 'Open a GitHub issue. Public changelog, public roadmap.', cta: 'Open issue on GitHub', href: 'https://github.com/freecodecollective/theoriginalgolinks/issues', color: 'mint' },
  { icon: '🌱', title: 'New here?', desc: 'Five minutes to go from never-heard-of-it to go/everything.', cta: 'How it works', to: '/#how', color: 'lemon' },
];

export default function SupportPage() {
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.sendSupportMessage({ name, email, subject, message });
      setSubmitted(true);
    } catch (err) {
      setError((err as ApiError).message || 'Could not send your message. Please email support@tidygolinks.com directly.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Shell>
      <PageHero
        title="Real humans, real answers"
        titleAccentWord="Real humans"
        sub="We're a small team and we actually read every message. No ticket queues, no tier-one deflection."
        decorShapes={[
          { shape: 'circle', size: 260, color: 'sky', style: { position: 'absolute', top: -40, left: -60, opacity: 0.5 } },
          { shape: 'pill', size: 260, color: 'peach', style: { position: 'absolute', top: 80, right: -80, opacity: 0.55, transform: 'rotate(18deg)' } },
          { shape: 'circle', size: 130, color: 'mint', style: { position: 'absolute', bottom: 10, left: '35%', opacity: 0.55 } },
        ]}
      />

      <C2Section pad={32} decor={cardsDecor}>
        <div className="grid grid-cols-2 gap-6 max-w-[960px] mx-auto">
          {cards.map(card => {
            const ctaInner = (
              <span style={{ color: 'var(--accent-deep)' }} className="text-[15px] font-semibold">{card.cta} →</span>
            );
            return (
              <C2Card key={card.title} pad={32} className="flex flex-col gap-3">
                <div
                  className="w-[52px] h-[52px] rounded-md flex items-center justify-center text-2xl mb-1"
                  style={{ background: pastelBg[card.color] }}
                >
                  {card.icon}
                </div>
                <h3 className="font-display font-bold text-ink m-0" style={{ fontSize: 22, letterSpacing: '-0.01em' }}>
                  {card.title}
                </h3>
                <p className="text-[15px] text-ink-soft m-0 leading-[1.6] flex-1">{card.desc}</p>
                {card.href ? (
                  <a href={card.href} className="no-underline text-left">{ctaInner}</a>
                ) : card.to ? (
                  <Link to={card.to} className="no-underline text-left">{ctaInner}</Link>
                ) : (
                  ctaInner
                )}
              </C2Card>
            );
          })}
        </div>
      </C2Section>

      <C2Section pad={64} bg="var(--color-surface)" decor={formDecor}>
        <div className="max-w-[580px] mx-auto">
          <h2
            className="font-display font-extrabold text-ink m-0 text-center"
            style={{ fontSize: 38, letterSpacing: '-0.02em' }}
          >
            Send us a <span style={{ color: 'var(--accent-deep)' }}>message</span>
          </h2>
          <p className="text-base text-ink-soft text-center mt-3 mb-9">
            Bug, feature idea, kudos — whatever's on your mind.
          </p>

          {submitted ? (
            <div
              className="border border-hairline rounded-xl text-center"
              style={{ background: 'var(--color-mint)', padding: '40px 32px' }}
            >
              <div className="text-5xl mb-3">✉</div>
              <h3 className="font-display font-bold text-ink m-0" style={{ fontSize: 24 }}>Message sent</h3>
              <p className="text-[15px] text-ink-soft mt-2 m-0">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
              <Field label="Name">
                <input type="text" required placeholder="Your name" style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
              </Field>
              <Field label="Email">
                <input type="email" required placeholder="you@company.com" style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} />
              </Field>
              <Field label="Subject">
                <input type="text" required placeholder="What's this about?" style={inputStyle} value={subject} onChange={e => setSubject(e.target.value)} />
              </Field>
              <Field label="Message">
                <textarea required placeholder="Tell us more…" rows={5} style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }} value={message} onChange={e => setMessage(e.target.value)} />
              </Field>
              {error && (
                <p className="text-[13px] m-0" style={{ color: 'var(--color-peach-deep)' }}>{error}</p>
              )}
              <C2Button variant="accent" size="lg" type="submit" className="mt-2 self-start" disabled={loading}>
                {loading ? 'Sending…' : 'Send message →'}
              </C2Button>
            </form>
          )}
        </div>
      </C2Section>
    </Shell>
  );
}
